import { dehydrate, HydrationBoundary } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import type { Definition } from '../../../../../schema/frameworks/entry.js';
import type { SettingsKeys } from '../../../../../schema/sessionstore/entry.js';
import { createQueryFn, settingsQueryKeys } from '../../../../lib/entry.js';
import { getCouponSettings } from '../../../actions/coupon/entry.js';
import { getSharedQueryClient } from '../../components/getSharedQueryProvider.js';

const couponSettingsFetcher = createQueryFn(getCouponSettings);

type CouponSettingsHydrationBoundaryProps = {
  product: Definition['moduleEngine'];
  settingsKey: SettingsKeys;
  children: ReactNode;
};

/**
 * Server component that prefetches coupon settings and seeds the Tanstack query cache for its subtree.
 * Mount this around client components using `useCouponSettings` when they render outside a
 * play/wager/result provider (for example in a parallel route slot), otherwise the suspense query
 * would call the server action during initial render, which Next.js does not allow.
 * This component should NEVER be async.
 *
 * @param root0 component props
 * @param root0.product Product to prefetch settings for
 * @param root0.settingsKey Settings key, to get settings for either play or my bets/results
 * @param root0.children React subtree that should read from the seeded cache
 * @returns Hydration boundary wrapping the children
 */
export function CouponSettingsHydrationBoundary({
  product,
  settingsKey,
  children,
}: CouponSettingsHydrationBoundaryProps): ReactNode {
  const queryClient = getSharedQueryClient();

  void queryClient.prefetchQuery({
    queryKey: settingsQueryKeys.product(product, settingsKey),
    queryFn: couponSettingsFetcher(),
  });

  return <HydrationBoundary state={dehydrate(queryClient)}>{children}</HydrationBoundary>;
}
